import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI, TokenUsageResponse } from '../../services/admin';
import { DeepAnalysisFullSession } from '../../services/api';
import { ConversationTimeline } from '../deep-analysis/ConversationTimeline';
import { mapFullSessionToConversationSession } from '../deep-analysis/utils/sessionMapper';
import TokenUsageCard from './TokenUsageCard';

interface AnalysisPreviewModalProps {
  sessionId: string;
  onClose: () => void;
}

export function AnalysisPreviewModal({ sessionId, onClose }: AnalysisPreviewModalProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
  const [fullSession, setFullSession] = useState<DeepAnalysisFullSession | null>(null);
  const [tokenUsage, setTokenUsage] = useState<TokenUsageResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [sessionData, tokenData] = await Promise.all([
          adminAPI.getSessionPreview(sessionId),
          adminAPI.getSessionTokenUsage(sessionId),
        ]);
        setFullSession(sessionData);
        setTokenUsage(tokenData);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [sessionId]);

  // Convert full session to ConversationSession using the same mapper as user view
  const session = fullSession ? mapFullSessionToConversationSession(fullSession) : null;
  const timeline = session?.timeline || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('analysisReview.analysisPreview')}
            </h3>
            <p className="text-sm text-slate-500">
              Widok użytkownika sesji {sessionId.substring(0, 8)}...
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
                <p className="mt-4 text-slate-600 dark:text-slate-400">{t('analysisReview.loadingPreview')}</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 max-w-md">
                {error}
              </div>
            </div>
          ) : (
            <>
              {/* Timeline area - main content */}
              <div className="flex-1 overflow-hidden flex flex-col bg-slate-100 dark:bg-slate-900">
                {timeline.length > 0 ? (
                  <ConversationTimeline
                    timeline={timeline}
                    // Read-only mode - no clarification handlers
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <p className="text-slate-500 dark:text-slate-400">
                      Brak danych do wyświetlenia
                    </p>
                  </div>
                )}
              </div>

              {/* Sidebar - token usage */}
              <div className="w-80 border-l border-slate-200 dark:border-slate-700 p-4 overflow-y-auto bg-white dark:bg-slate-800">
                <h4 className="font-medium text-slate-900 dark:text-white mb-4">
                  Informacje o sesji
                </h4>

                {/* Status */}
                <div className="mb-4">
                  <span className="text-sm text-slate-500 dark:text-slate-400">Status:</span>
                  <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                    fullSession?.status === 'complete'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : fullSession?.status === 'error'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                  }`}>
                    {fullSession?.status || 'nieznany'}
                  </span>
                </div>

                {/* Dates */}
                <div className="space-y-2 text-sm mb-4">
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Utworzono:</span>
                    <span className="ml-2 text-slate-900 dark:text-white">
                      {fullSession?.createdAt
                        ? new Date(fullSession.createdAt).toLocaleString(locale)
                        : '-'}
                    </span>
                  </div>
                  {fullSession?.completedAt && (
                    <div>
                      <span className="text-slate-500 dark:text-slate-400">Zakończono:</span>
                      <span className="ml-2 text-slate-900 dark:text-white">
                        {new Date(fullSession.completedAt).toLocaleString(locale)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Token Usage Card */}
                <TokenUsageCard tokenUsage={tokenUsage} />

                {/* Report indicator */}
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-2">
                    {fullSession?.finalReport ? (
                      <>
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-green-600 dark:text-green-400">
                          Raport wygenerowany
                        </span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          Brak raportu
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            {t('analysisReview.closePreview')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AnalysisPreviewModal;
